import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import { TabContent, TabPane } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex } from "antd"
import { UserOutlined } from "@ant-design/icons"
import Header from "components/accounting/Header"
import RangeSelector from "components/accounting/RangeSelector"
import Dashboard from "components/accounting/Dashboard"
import Details from "components/accounting/Details"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { CustomSelect } from "ui/Input"

import useSearchFilter from "hooks/useSearchFilter"
import { useTitle } from "hooks/useTitle"
import { isEqual } from "lodash"

import { ACCESS_ATTRIBUTE, OVERVIEW_RANGE_OPTIONS } from "constants/accounting"
import { getCrewProfiles } from "store/crew-profiles"

const RANGE_MENU_OPTIONS = Object.entries(OVERVIEW_RANGE_OPTIONS).map(([value, label]) => ({ value, label }))

export default function Accounting() {
  useTitle("Accounting")
  useLoaderWatchers({ watchers: ["accounting"] })
  const dispatch = useDispatch()
  const [params, onChangeFilters] = useSearchFilter({ accessAttributes: ACCESS_ATTRIBUTE })
  const [paramsState, setParamsState] = useState(params)

  const changeHandler = useCallback(
    ({ target }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value
      })),
    []
  )

  useEffect(() => {
    if (isEqual(paramsState, params)) return
    onChangeFilters(paramsState)
  }, [paramsState, params, onChangeFilters])

  const isTipReport = params.type === "tip_report"

  useEffect(() => {
    if (isTipReport) dispatch(getCrewProfiles())
  }, [isTipReport, dispatch])

  const crewProfileItems = useSelector((state) => state.crewProfiles.items || [])

  const crewOptions = useMemo(
    () => crewProfileItems.map((m) => ({ value: m.id, label: m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() })),
    [crewProfileItems]
  )

  const crewIdsParam = params?.["crew_ids[]"]
  let selectedCrewIds = []
  if (Array.isArray(crewIdsParam)) {
    selectedCrewIds = crewIdsParam
  } else if (crewIdsParam) {
    selectedCrewIds = [crewIdsParam]
  }
  const includeUnassigned = params?.include_unassigned || false

  const handleCrewChange = (values) => changeHandler({ target: { name: "crew_ids[]", value: values } })
  const handleIncludeUnassignedToggle = () => changeHandler({ target: { name: "include_unassigned", value: !includeUnassigned } })

  const activeTab = params.type === "business_overview" ? "dashboard" : "details"
  const isBusinessOverview = params.type === "business_overview"

  return (
    <div className="flex-fill z-0 position-relative px-20 py-20" id="accounting">
      <Header params={params} onChangeFilters={changeHandler} />
      <Flex align="center" gap={8} justify="end" wrap className="my-m">
        {isTipReport && (
          <>
            <CustomSelect
              mode="multiple"
              placeholder="Crew members"
              value={selectedCrewIds}
              onChange={handleCrewChange}
              options={crewOptions}
              allowClear
              style={{ minWidth: 200 }}
              maxTagCount="responsive"
            />
            <Button
              color="primary"
              variant={includeUnassigned ? "solid" : "outlined"}
              type={includeUnassigned ? "primary" : "default"}
              icon={<UserOutlined />}
              onClick={handleIncludeUnassignedToggle}
            >
              Include unassigned
            </Button>
          </>
        )}
        {isBusinessOverview && (
          <CustomSelect
            value={params.overview_range || "current_month"}
            onChange={(value) => changeHandler({ target: { name: "overview_range", value } })}
            options={RANGE_MENU_OPTIONS}
          />
        )}
        <RangeSelector params={params} onChangeFilters={changeHandler} />
      </Flex>
      <FirstLoading name="accounting">
        <TabContent activeTab={activeTab} className="w-100">
          <TabPane tabId="dashboard">
            <Dashboard params={params} onChangeFilters={changeHandler} />
          </TabPane>
          <TabPane tabId="details">
            <Details params={params} onChangeFilters={changeHandler} />
          </TabPane>
        </TabContent>

        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>
    </div>
  )
}
